/**
 * useAnimationSound Hook
 * 
 * Manages sound playback for candy animations.
 * Preloads sounds, plays on phase changes, respects intensity tiers.
 */

import { useCallback, useEffect, useRef } from 'react';
import { getSoundsForPhase } from '../components/CandyShop/animations/soundRegistry';

const SILENT_AUDIO_SRC =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

let audioUnlocked = false;
let unlockPromise = null;
let unlockListenersAttached = false;
let autoplayWarningShown = false;
const pendingPlays = [];

const isAutoplayError = (err) => err && err.name === 'NotAllowedError';
const hasUserActivation = () =>
    typeof navigator !== 'undefined' &&
    navigator.userActivation &&
    navigator.userActivation.hasBeenActive;

const flushPendingPlays = () => {
    if (!audioUnlocked || pendingPlays.length === 0) return;

    const queue = pendingPlays.splice(0, pendingPlays.length);
    queue.forEach(({ audio, path }) => {
        try {
            audio.currentTime = 0;
            audio.volume = 0.7;
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch((err) => {
                    if (!isAutoplayError(err)) {
                        console.warn(`Failed to play sound: ${path}`, err);
                    }
                });
            }
        } catch (err) {
            console.warn(`Failed to play sound: ${path}`, err);
        }
    });
};

const unlockAudio = () => {
    if (audioUnlocked) return Promise.resolve(true);
    if (unlockPromise) return unlockPromise;

    try {
        const audio = new Audio(SILENT_AUDIO_SRC);
        audio.volume = 0;
        unlockPromise = audio.play()
            .then(() => {
                audio.pause();
                audioUnlocked = true;
                unlockPromise = null;
                flushPendingPlays();
                return true;
            })
            .catch(() => {
                unlockPromise = null;
                return false;
            });
    } catch {
        unlockPromise = null;
        return Promise.resolve(false);
    }

    return unlockPromise;
};

const ensureUnlockListeners = () => {
    if (audioUnlocked || unlockListenersAttached || typeof window === 'undefined') return;

    unlockListenersAttached = true;
    const handler = () => {
        unlockAudio();
    };

    window.addEventListener('pointerdown', handler, { once: true, capture: true, passive: true });
    window.addEventListener('keydown', handler, { once: true, capture: true });
    window.addEventListener('touchstart', handler, { once: true, capture: true, passive: true });
    window.addEventListener('click', handler, { once: true, capture: true });

    if (hasUserActivation()) {
        unlockAudio();
    }
};

const primeAnimationAudio = () => {
    ensureUnlockListeners();
    return unlockAudio();
};

/**
 * Hook for managing animation sounds
 * @param {string} candyType - The candy type (CocoaPop, SlapTaffy, etc.)
 * @param {number} intensity - The intensity level (1-6)
 * @param {boolean} enabled - Whether sound is enabled (default: true)
 * @param {string} flowId - Optional flow ID for DryPull (random flow selection)
 * @returns {Object} - { playPhaseSound, preloadSounds, stopAllSounds }
 */
const useAnimationSound = (candyType, intensity = 1, enabled = true, flowId = null) => {
    const audioRefs = useRef({});
    const loadedSounds = useRef(new Set());

    const playAudio = useCallback((audio, path) => {
        // Skip if this audio is already playing (prevents double-play/stutter)
        if (!audio.paused && audio.currentTime > 0) return;

        audio.currentTime = 0;
        audio.volume = 0.7;

        if (!audioUnlocked && hasUserActivation()) {
            unlockAudio();
        }

        try {
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise
                    .then(() => {
                        audioUnlocked = true;
                    })
                    .catch((err) => {
                        if (isAutoplayError(err)) {
                            if (!autoplayWarningShown) {
                                console.warn('Sound playback blocked until user interaction.');
                                autoplayWarningShown = true;
                            }
                            pendingPlays.push({ audio, path });
                            ensureUnlockListeners();
                            unlockAudio();
                            return;
                        }
                        console.warn(`Failed to play sound: ${path}`, err);
                    });
            }
        } catch (err) {
            console.warn(`Failed to play sound: ${path}`, err);
        }
    }, []);

    // Preload sounds for all phases
    const preloadSounds = useCallback((phases) => {
        if (!enabled || !candyType) return;

        ensureUnlockListeners();

        phases.forEach((phase) => {
            const soundPaths = getSoundsForPhase(candyType, phase, intensity, flowId);
            soundPaths.forEach((path) => {
                if (!loadedSounds.current.has(path)) {
                    try {
                        const audio = new Audio();
                        audio.preload = 'auto';
                        // Dynamically import the sound file
                        import(`../components/CandyShop/animations/animsounds/${path.replace('./animsounds/', '')}`).then((module) => {
                            audio.src = module.default;
                            audioRefs.current[path] = audio;
                            loadedSounds.current.add(path);
                        }).catch((err) => {
                            console.warn(`Failed to load sound: ${path}`, err);
                        });
                    } catch (err) {
                        console.warn(`Failed to preload sound: ${path}`, err);
                    }
                }
            });
        });
    }, [candyType, intensity, enabled, flowId]);

    // Play sounds for a specific phase
    const playPhaseSound = useCallback((phase) => {
        if (!enabled || !candyType) return;

        ensureUnlockListeners();

        const soundPaths = getSoundsForPhase(candyType, phase, intensity, flowId);

        soundPaths.forEach((path) => {
            const audio = audioRefs.current[path];
            if (audio) {
                playAudio(audio, path);
            } else {
                // Try to play even if not preloaded
                try {
                    import(`../components/CandyShop/animations/animsounds/${path.replace('./animsounds/', '')}`).then((module) => {
                        const newAudio = new Audio(module.default);
                        playAudio(newAudio, path);
                        audioRefs.current[path] = newAudio;
                    }).catch((err) => {
                        console.warn(`Failed to load sound for playback: ${path}`, err);
                    });
                } catch (err) {
                    console.warn(`Failed to dynamically load sound: ${path}`, err);
                }
            }
        });
    }, [candyType, intensity, enabled, flowId, playAudio]);

    // Stop all playing sounds
    const stopAllSounds = useCallback(() => {
        Object.values(audioRefs.current).forEach((audio) => {
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
            }
        });
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        if (enabled) {
            ensureUnlockListeners();
            if (hasUserActivation()) {
                unlockAudio();
            }
        }

        return () => {
            stopAllSounds();
            audioRefs.current = {};
            loadedSounds.current.clear();
        };
    }, [enabled, stopAllSounds]);

    return {
        playPhaseSound,
        preloadSounds,
        stopAllSounds,
    };
};

export { primeAnimationAudio };
export default useAnimationSound;
